import React from "react";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Grid2 from "@mui/material/Unstable_Grid2";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Delete from "@mui/icons-material/Delete";
import Undo from "@mui/icons-material/Undo";
import { GridRowParams } from "@mui/x-data-grid-premium";
import { useTranslation } from "react-i18next";
import { WorkShiftTimeSlotItem } from "../../models/workShitTimeSlot/workShiftTimeSlotItem";
import { IWorkHoursItem } from "../../models/workTime/workHoursItem";
import { deletableCategories } from "../../models/workTime/hourBookingCategory";
import { selectSalaryRowTypes } from "../../store/workShiftTimeSlotSlice";
import { useAppSelector } from "../../framework/customStore";
import { AppUtils } from "../../models/common/appUtils";
import _groupBy from "lodash/groupBy";
import _map from "lodash/map";

interface WorkTimeEntryDetailsPanelProps {
    workHours: IWorkHoursItem[];
    onToggleDeleteWorkHour: (
        id: string,
        category: string,
        restore: boolean
    ) => void;
    ignoredCategoriesMap: Record<string, number[]>;
}

interface PayrollRow {
    id: string;
    workShiftTimeSlotId: string;
    category: string;
    amount: number;
    salaryRowTypeName?: string;
    salaryRowTypeId?: string;
    deleted: boolean;
}

export const WorkTimeEntryDetailsPanel = ({
    row,
    workHours,
    onToggleDeleteWorkHour,
    ignoredCategoriesMap,
}: WorkTimeEntryDetailsPanelProps & GridRowParams<WorkShiftTimeSlotItem>) => {
    const { t } = useTranslation();
    const salaryRowTypes = useAppSelector(selectSalaryRowTypes);

    const slotWorkHours = workHours.filter(
        (h) => h.workShiftTimeSlotId === row.id
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const grouped: Record<string, IWorkHoursItem[]> = _groupBy(
        slotWorkHours,
        (h) => `${h.workShiftTimeSlotId}.${h.categoryId}`
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    const payrollRows: PayrollRow[] = _map(
        grouped,
        (hours: IWorkHoursItem[]) => {
            const first = hours[0];
            const srt = salaryRowTypes.find(
                (s) => s.id === first.salaryRowTypeId
            );
            return {
                id: `${row.id}.${first.categoryId}_${first.salaryRowTypeId}_${first.startTime}`,
                workShiftTimeSlotId: row.id,
                category: first.category,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                amount: hours.reduce(
                    (acc: number, cur: IWorkHoursItem) => acc + cur.amount,
                    0
                ),
                salaryRowTypeName: srt?.name ?? first.salaryRowTypeName,
                salaryRowTypeId: first.salaryRowTypeId,
                deleted: false,
            };
        }
    );

    const ignoredRows: PayrollRow[] = (ignoredCategoriesMap[row.id] ?? []).map(
        (c) => ({
            id: `${row.id}.deleted-${c}`,
            workShiftTimeSlotId: row.id,
            category: c.toString(),
            amount: 0,
            deleted: true,
        })
    );

    const allPayrollRows = [...payrollRows, ...ignoredRows];

    const getAmountLabel = (payrollRow: PayrollRow): string => {
        if (payrollRow.deleted) return "";
        const srt = salaryRowTypes.find(
            (s) => s.id === payrollRow.salaryRowTypeId
        );
        if (srt) {
            return AppUtils.getDisplayedNumberBasedOnUnit(
                payrollRow.amount,
                srt.measureUnit,
                srt.decimals
            );
        }
        return AppUtils.getDurationStrByDurationMinShort(payrollRow.amount);
    };

    const getCategoryLabel = (payrollRow: PayrollRow): string =>
        t(`hourBookingCategory.${payrollRow.category}`);

    const hasPayrollRows = allPayrollRows.length > 0;
    const hasDetails = row.comment;
    const hasAddress = !!(row.startLocationName || row.endLocationName);
    const hasSideColumn = hasDetails || hasAddress;

    if (!hasPayrollRows && !hasSideColumn) return null;

    return (
        <Box
            fontSize="small"
            sx={{
                p: 1,
                backgroundColor: "lightBlue.main",
                width: "100%",
                height: "100%",
            }}
        >
            <Grid2 container spacing={1}>
                {hasPayrollRows && (
                    <Grid2 xs={12} md="auto" px={1} sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                        >
                            {t("workTime.calculatedWorkHours")}
                        </Typography>
                        {allPayrollRows.map((payrollRow) => {
                            const isDeletable =
                                !payrollRow.deleted &&
                                deletableCategories().includes(
                                    payrollRow.category
                                );
                            const isDeleted = payrollRow.deleted;

                            return (
                                <Box
                                    key={payrollRow.id}
                                    display="flex"
                                    alignItems="center"
                                    gap={0.5}
                                    sx={{
                                        minHeight: 28,
                                        width: "fit-content",
                                        borderRadius: 1,
                                        px: 0.5,
                                        "&:hover": {
                                            backgroundColor:
                                                "rgba(0, 0, 0, 0.06)",
                                        },
                                    }}
                                >
                                    <Box
                                        sx={
                                            isDeleted
                                                ? {
                                                      textDecoration:
                                                          "line-through",
                                                      color: "text.disabled",
                                                  }
                                                : {}
                                        }
                                    >
                                        <span>
                                            {getCategoryLabel(payrollRow)}
                                        </span>
                                        {!isDeleted && (
                                            <span
                                                style={{
                                                    marginLeft: 8,
                                                    color: "text.secondary",
                                                }}
                                            >
                                                {getAmountLabel(payrollRow)}
                                            </span>
                                        )}
                                    </Box>
                                    {(isDeletable || isDeleted) && (
                                        <IconButton
                                            size="small"
                                            onClick={() =>
                                                onToggleDeleteWorkHour(
                                                    payrollRow.workShiftTimeSlotId,
                                                    payrollRow.category,
                                                    isDeleted
                                                )
                                            }
                                        >
                                            {isDeleted ? (
                                                <Tooltip
                                                    title={t("common.restore")}
                                                >
                                                    <Undo fontSize="small" />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip
                                                    title={t("common.remove")}
                                                >
                                                    <Delete
                                                        fontSize="small"
                                                        color="error"
                                                    />
                                                </Tooltip>
                                            )}
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })}
                    </Grid2>
                )}
                {hasPayrollRows && hasSideColumn && (
                    <Grid2 xs="auto" display={{ xs: "none", md: "block" }}>
                        <Divider orientation="vertical" aria-hidden="true" />
                    </Grid2>
                )}
                {hasDetails && (
                    <Grid2 xs={12} md="auto" px={1} sx={{ flex: 1 }}>
                        <Typography
                            variant="caption"
                            fontWeight="bold"
                            display="block"
                            mb={0.5}
                        >
                            {t("common.description")}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ whiteSpace: "pre-wrap" }}
                        >
                            {row.comment}
                        </Typography>
                    </Grid2>
                )}
                {hasDetails && hasAddress && (
                    <Grid2 xs="auto" display={{ xs: "none", md: "block" }}>
                        <Divider orientation="vertical" aria-hidden="true" />
                    </Grid2>
                )}
                {hasAddress && (
                    <Grid2 xs={12} md="auto" px={1} sx={{ flex: 1 }}>
                        {row.startLocationName && (
                            <Box mb={row.endLocationName ? 1 : 0}>
                                <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    display="block"
                                    mb={0.5}
                                >
                                    {t("common.startLocation")}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {row.startLocationName}
                                </Typography>
                            </Box>
                        )}
                        {row.endLocationName && (
                            <Box>
                                <Typography
                                    variant="caption"
                                    fontWeight="bold"
                                    display="block"
                                    mb={0.5}
                                >
                                    {t("common.endLocation")}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{ whiteSpace: "pre-wrap" }}
                                >
                                    {row.endLocationName}
                                </Typography>
                            </Box>
                        )}
                    </Grid2>
                )}
            </Grid2>
        </Box>
    );
};
